/****************************************************
 * Module Name:  shapeMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { Component } from "react";
import ShapeProxy from "../../utility/proxy/manager/shapeProxy";
import AddShapeModal from "./addShapeModal";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Box, Button, Tooltip } from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import renderCellExpand from "../common/dataGridCellExpand";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import CommonSnackbar from "../common/commonSnackBar";
import NoPermission from "../common/noPermission";
import PageTitle from "../common/pageTitle";

export class ShapeMaster extends Component {
  constructor(props) {
    super(props);

    this.state = {
      shapes: [],
      showAddShapeModal: false,
      loadingButton: false,
      editOperation: false,
      sourceData: {},
      errorSnackbar: false,
      accesDenied: false,
      showShapeLoadSpinner: true,
      local_Permission: Constants.USER_PERMISSION.filter(
        (iPermission) =>
          iPermission.permissionid === Constants.PERMISSIONS.MASTER_SHAPE
      ),
    };

    this.checkPermission()
  }

  componentDidMount() {
    PageTitle("MASTER_SHAPE");
  }

  checkPermission = () => {
    let allowed = Utility.isReadAllowed(this.state.local_Permission)
    if (!allowed) {
      this.setState({
        accesDenied: true
      })
    } else {
      this.fetchShapeData(false);
    }
  }

  fetchShapeData = (bAnimateSpinner) => {
    bAnimateSpinner && this.setState({ showShapeLoadSpinner: true });
    ShapeProxy.GetAllShape((responseData) => {
      if (responseData) {
        this.setState({ shapes: responseData, showShapeLoadSpinner: false })
      } else {
        this.setState({ errorSnackbar: true, showShapeLoadSpinner: false })
      }
    }
    );
  };

  columns = [
    {
      field: "id",
      headerName: "ID",
      headerAlign: "center",
      type: 'number',
      align: "center",
    },
    {
      field: "name",
      headerName: "SHAPE",
      headerAlign: "center",
      align: "center",
      width: 80,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.shape.sort((a, b) => a.seq - b.seq).map(option => option.value),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "fullname",
      headerName: "FULLNAME",
      headerAlign: "center",
      align: "center",
      flex: 1,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.shape.sort((a, b) => a.seq - b.seq).map(option => option.label),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "description",
      headerName: "DESCRIP",
      headerAlign: "center",
      align: "center",
      flex: 1,
      type: "singleSelect",
      valueOptions: Constants.uniqueFilterValues.shape.sort((a, b) => a.seq - b.seq).map(option => option.label),
      filterOperators: Utility.combinedFilters(),
      renderCell: renderCellExpand,
    },
    {
      field: "image",
      headerName: "IMAGE",
      headerAlign: "center",
      align: "center",
      flex: 1,
      renderCell: (params) => (
        <img
          src={`data:image/svg+xml;base64,${params.row.image}`}
          alt=""
          style={{ display: 'block', textAlign: "center", maxWidth: "100px" }}
        />
      ),
    },
    {
      field: "packetimage1",
      headerName: "PACKET IMAGE 1",
      headerAlign: "center",
      align: "center",
      flex: 1,
      renderCell: (params) => (
        <img
          src={`data:image/svg+xml;base64,${params.row.packetimage1}`}
          alt=""
          style={{ display: 'block', textAlign: "center", maxWidth: "100px" }}
        />
      ),
    },
    {
      field: "packetimage2",
      headerName: "PACKET IMAGE 2",
      headerAlign: "center",
      align: "center",
      flex: 1,
      renderCell: (params) => (
        <img
          src={`data:image/svg+xml;base64,${params.row.packetimage2}`}
          alt=""
          style={{ display: 'block', textAlign: "center", maxWidth: "100px" }}
        />
      ),
    },
    {
      field: "packetimage3",
      headerName: "PACKET IMAGE 3",
      headerAlign: "center",
      align: "center",
      flex: 1,
      renderCell: (params) => (
        <img
          src={`data:image/svg+xml;base64,${params.row.packetimage3}`}
          alt=""
          style={{ display: 'block', textAlign: "center", maxWidth: "100px" }}
        />
      ),
    },
    {
      field: "disabled",
      headerName: "DISABLED",
      headerAlign: "center",
      align: "center",
      type: "boolean",
      flex: 1
    },
    {
      field: "seq",
      headerName: "SEQUENCE",
      headerAlign: "center",
      align: "center",
      type: 'number',
      flex: 1
    },
    {
      field: "edit",
      headerName: "EDIT",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <Box
            style={{ textAlign: "center", cursor: "pointer" }}
            onClick={() => {
              this.setState({
                showAddShapeModal: true,
                editOperation: true,
                sourceData: params.row,
              });
            }}
          >
            {" "}
            <BorderColorIcon color="primary" />
          </Box>
        );
      },
    },
  ];

  hideAddShapeModal = () => {
    this.setState({
      showAddShapeModal: false,
    });
  };

  handleValidation = (shapeData) => {
    //TODO do validation if needed here
    return true;
  };

  addShape = (shapeData, formData) => {
    this.setState({ loadingButton: true });
    if (this.state.editOperation) {
      //Updating Shape, and once shape is updated on call refresh the list of current shapes
      ShapeProxy.UpdateShape(shapeData, (newShape) => {
        if (!newShape) {
          this.setState({ errorSnackbar: true, loadingButton: false });
        }
        if (formData) {
          //Updating Shape, and once shape is updated on call refresh the list of current shapes
          ShapeProxy.UploadShapeImage(newShape.id, formData, (response) => {
            if (response) {
              this.fetchShapeData(true);
              this.setState({ loadingButton: false });
              //Hidding shape modal
              this.hideAddShapeModal();
            } else {
              this.setState({ errorSnackbar: true, loadingButton: false });
            }
          });
        } else {
          this.fetchShapeData(true);
          this.setState({ loadingButton: false });
          //Hidding shape modal
          this.hideAddShapeModal();
        }
      });
    } else {
      //Creating new Shape, and once shape is created on call refresh the list of current shapes
      ShapeProxy.AddShape(shapeData, (newShape) => {
        if (!newShape) {
          this.setState({ errorSnackbar: true, loadingButton: false });
        }

        if (formData) {
          //Creating new Shape, and once shape is created on call refresh the list of current shapes
          ShapeProxy.UploadShapeImage(newShape.id, formData, (response) => {
            if (response) {
              this.fetchShapeData(true);
              this.setState({ loadingButton: false });
              //Hidding shape modal
              this.hideAddShapeModal();
            } else {
              this.setState({ errorSnackbar: true, loadingButton: false });
            }
          });
        } else {
          this.fetchShapeData(true);
          this.setState({ loadingButton: false });
          //Hidding shape modal
          this.hideAddShapeModal();
        }
      });
    }
  };

  render() {
    const CustomToolbar = () => {
      return (
        <GridToolbarContainer
          sx={{
            display: "flex",
            alignItems: "center",
            marginHorizontal: "5px",
            "&.MuiDataGrid-toolbarContainer": {
              padding: "0px 4px !important",
            },
          }}
        >
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarExport />
          <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
          {this.state.showShapeLoadSpinner ? (
            <CircularProgress size="1rem" />
          ) : (
            <Tooltip title="Reload List">
              <RefreshOutlinedIcon
                color="primary"
                fontSize="medium"
                onClick={() => this.fetchShapeData(true)}
                sx={{ cursor: "pointer" }}
              />
            </Tooltip>
          )}
          {Utility.isAddAllowed(this.state.local_Permission) ? (
            <Tooltip title="Add Shape">
              <Button
                color="info"
                variant="outlined"
                size="small"
                sx={{ ml: "5px" }}
                onClick={() =>
                  this.setState({
                    showAddShapeModal: true,
                    editOperation: false,
                    sourceData: {},
                  })
                }
                startIcon={<AddBoxOutlinedIcon size="large" />}
              >
                Shape
              </Button>
            </Tooltip>
          ) : null}
          <GridToolbarQuickFilter debounceMs={500} />
        </GridToolbarContainer>
      );
    }
    let allowed = Utility.isReadAllowed(this.state.local_Permission)
    if (!allowed) {
      return <NoPermission />
    }
    const rows = this.state.shapes;
    const columns = Utility.isUpdateAllowed(this.state.local_Permission)
      ? this.columns
      : this.columns.filter((col) => col.field !== "edit");
    return (
      <Box>
        <CommonSnackbar
          show={this.state.errorSnackbar}
          message={<Box>{Constants.APiFailErrorMessage}</Box>}
          variant={"error"}
          autoHide={Constants.SnackBarCloseTimer}
          handleClose={() => this.setState({ errorSnackbar: false })}
          title="Failed"
        />
        <br />
        {this.state.showAddShapeModal ? (
          <AddShapeModal
            Show={true}
            handleClose={this.hideAddShapeModal}
            handleSave={this.addShape}
            handleValidation={this.handleValidation}
            editOperation={this.state.editOperation}
            sourceData={this.state.sourceData}
            loading={this.state.loadingButton}
          />
        ) : (
          <Box />
        )}
        <Box px={1}>
          <Box
            sx={{
              backgroundColor: '#fff',
              color: '#000',
              width: "100%",
              position: "relative",
              "& .MuiTablePagination-selectLabel": {
                marginTop: "15px",
              },
              "& .MuiTablePagination-displayedRows": {
                marginTop: "15px",
              },
              "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                fontWeight: Constants.defaultFontWeight,
              },
              "& .css-cc8tf1": {
                fontWeight: Constants.defaultFontWeight,
              },
              height: "85vh",
            }}
          >
            <DataGridPro
              disableRowSelectionOnClick={true}
              getRowHeight={() => "auto"}
              density="compact"
              loading={this.state.showShapeLoadSpinner}
              sx={{
                '& .MuiDataGrid-columnHeader': {
                  color: '#fff',
                  backgroundColor: 'grey',
                },
                "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                  py: "4px",
                },
                "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                  py: "12px",
                },
                "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                  py: "18px",
                },
                fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
              }}
              slots={{ toolbar: CustomToolbar }}
              rows={rows}
              columns={columns}
              getRowId={(row) => row.id}
              rowSpacingType="border"
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    // Hide columns status and traderName, the other columns will remain visible
                    id: false,
                  },
                },
                pagination: {
                  paginationModel: Constants.pageSize,
                },
              }}
              pageSizeOptions={Constants.pageSizeOptions}
              pagination
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }
}

export default ShapeMaster;
